/**
 * Strip personal data out of anything on its way to Sentry.
 *
 * Why this exists: the three Sentry configs used to run with `sendDefaultPii: true` and
 * `enableLogs: true`, which sent members' email addresses, phone numbers and IP addresses to a
 * third party that is not named in the privacy policy. `sendDefaultPii` is off now, but that alone
 * is not enough, because it only governs what the SDK attaches automatically. Anything a developer
 * puts into an error message, a breadcrumb or a `console.log` still goes through untouched, and the
 * auth routes have a long history of doing exactly that.
 *
 * So this is the backstop rather than the fix: the fix is not logging the address in the first
 * place. Treat a redaction appearing in Sentry as a bug to go and correct at the source.
 *
 * A user is still identifiable by their id, which is what is actually useful when debugging.
 */

const EMAIL = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
// E.164 and common UK formats. Deliberately loose: over-redacting a log line costs nothing.
const PHONE = /(?:\+\d{1,3}[\s-]?)?(?:\(?\d{2,5}\)?[\s-]?){2,5}\d{2,4}/g;
// Bearer tokens, JWTs, Supabase keys and Turnstile tokens have all been logged verbatim before.
const JWT = /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g;
const BEARER = /\b(?:Bearer|Basic)\s+[A-Za-z0-9._~+/-]{12,}=*/gi;

/** Redact personal data in a single string. Exported so route handlers can use it directly. */
export function scrubString(value: string): string {
  return value
    .replace(JWT, "[redacted-token]")
    .replace(BEARER, "[redacted-token]")
    .replace(EMAIL, "[redacted-email]")
    .replace(PHONE, (match) => (match.replace(/\D/g, "").length >= 9 ? "[redacted-phone]" : match));
}

/** Keys whose values are always dropped, whatever they contain. */
const SENSITIVE_KEY =
  /(email|phone|password|token|secret|authorization|cookie|session|otp|selfie|date_of_birth|dob|postcode|address)/i;

function scrubValue(value: unknown, depth: number): unknown {
  if (depth > 6) return value;
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY.test(key) ? "[redacted]" : scrubValue(inner, depth + 1);
    }
    return out;
  }
  return value;
}

/**
 * Scrub a Sentry event in place-ish and return it. Covers the fields that in practice carry
 * personal data: the exception message, the log message, breadcrumbs, extra/contexts, the request
 * (headers, cookies, query string, body) and the user object.
 */
export function scrubEvent<T extends Record<string, any>>(event: T): T {
  const e = event as Record<string, any>;

  if (Array.isArray(e.exception?.values)) {
    for (const value of e.exception.values) {
      if (typeof value?.value === "string") value.value = scrubString(value.value);
    }
  }

  if (typeof e.message === "string") e.message = scrubString(e.message);
  if (typeof e.logentry?.message === "string") {
    e.logentry.message = scrubString(e.logentry.message);
  }

  if (Array.isArray(e.breadcrumbs)) {
    for (const crumb of e.breadcrumbs) {
      if (typeof crumb?.message === "string") crumb.message = scrubString(crumb.message);
      if (crumb?.data) crumb.data = scrubValue(crumb.data, 0);
    }
  }

  if (e.extra) e.extra = scrubValue(e.extra, 0);
  if (e.contexts) e.contexts = scrubValue(e.contexts, 0);
  if (e.tags) e.tags = scrubValue(e.tags, 0);

  if (e.request) {
    delete e.request.cookies;
    if (e.request.headers) delete e.request.headers;
    if (typeof e.request.query_string === "string") {
      e.request.query_string = scrubString(e.request.query_string);
    }
    if (e.request.data) e.request.data = scrubValue(e.request.data, 0);
  }

  // Keep the id, which is what makes an issue actionable. Drop everything that identifies a person.
  if (e.user) {
    e.user = e.user.id ? { id: e.user.id } : undefined;
  }

  return e as T;
}
